import { create } from "zustand";
import { ProjectLock } from "@/backend/models/projectLock";
import { ProjectLockFrontendController } from "../controllers/projectLockController";
import { CreateProjectLockRequest, UpdateProjectLockRequest } from "@/types/projectLockTypes";

const initialState: { projectLocks: ProjectLock[] } = {
  projectLocks: [],
};

export interface ProjectLockStore {
  projectLocks: ProjectLock[];
  fetchProjectLocks: () => Promise<void>;
  fetchProjectLocksByProjectIds: (projectIds: string[]) => Promise<void>;
  createProjectLock: (data: CreateProjectLockRequest) => Promise<void>;
  updateProjectLock: (id: string, data: UpdateProjectLockRequest) => Promise<void>;
  deleteProjectLock: (id: string) => Promise<void>;
  reset: () => void;
}

export const useProjectLockStore = create<ProjectLockStore>((set) => ({
  ...initialState,
  fetchProjectLocks: async () => {
    const response = await new ProjectLockFrontendController().readAll();
    if (response.status === 200) {
      set({ projectLocks: response.projectLocks.map((p) => new ProjectLock(p)) });
    }
  },

  fetchProjectLocksByProjectIds: async (projectIds: string[]) => {
    const response = await new ProjectLockFrontendController().readByProjectIds(projectIds);
    if (response.status === 200) {
      set({ projectLocks: response.projectLocks.map((p) => new ProjectLock(p)) });
    }
  },

  createProjectLock: async (data: CreateProjectLockRequest) => {
    const response = await new ProjectLockFrontendController().create(data);

    if (response.status === 201 && response.projectLock) {
      const projectLock = new ProjectLock(response.projectLock);
      const current = useProjectLockStore.getState().projectLocks;
      set({ projectLocks: [...current, projectLock] });
    }
  },

  updateProjectLock: async (id: string, data: UpdateProjectLockRequest) => {
    const response = await new ProjectLockFrontendController().update(id, data);

    if (response.status === 200 && response.projectLock) {
      const projectLock = new ProjectLock(response.projectLock);
      const current = useProjectLockStore.getState().projectLocks;
      set({ projectLocks: current.map((p) => p.id === id ? projectLock : p) });
    }
  },

  deleteProjectLock: async (id: string) => {
    const response = await new ProjectLockFrontendController().delete(id);

    if (response.status === 200) {
      const current = useProjectLockStore.getState().projectLocks;
      set({ projectLocks: current.filter((p) => p.id !== id) });
    }
  },

  reset: () => set({ ...initialState }),
}));
